<template>
	<div
		:class="`formulate-input-element formulate-input-element--${context.type}`"
		:data-type="context.type">
		<b-row v-if="form.sport_activity && form.sport_activity.includes('football') && applicationType !== 'FB'">
			<b-col>
				<b-alert show variant="warning">
					<strong>Heads up!</strong> If you are requesting coverage for ONLY Football and Cheer <strong>teams, league or an association</strong>, please use the correct application.
					<Link :href="route('form.show', {'slug': 'football'})">
						<strong>Click here to start a football application.</strong>
					</Link>
				</b-alert>
			</b-col>
		</b-row>
		<b-row v-if="form.sport_activity && form.sport_activity.includes('baseball') && applicationType !== 'BB'">
			<b-col>
				<b-alert show variant="warning">
					<strong>Heads up!</strong> If you are requesting coverage for ONLY Baseball and Softball <strong>teams, league or an association</strong>, please use the correct application.
					<Link :href="route('form.show', {'slug': 'baseball-softball'})">
						<strong>Click here to start a Baseball/Softball application.</strong>
					</Link>
				</b-alert>
			</b-col>
		</b-row>
		<b-row v-if="form.sport_activity && form.sport_activity.includes('lacrosse') && applicationType !== 'LX'">
			<b-col>
				<b-alert show variant="warning">
					<strong>Heads up!</strong> If you are requesting coverage for ONLY Lacrosse <strong>teams, league or an association</strong>, please use the correct application.
					<Link :href="route('form.show', {'slug': 'lacrosse'})">
						<strong>Click here to start a Lacrosse application.</strong>
					</Link>
				</b-alert>
			</b-col>
		</b-row>
		<b-row>
			<b-col lg="6">
				<b-form-group :label="label">
					<b-form-select
						v-model="form.sport_activity"
						name="sport_activity"
						:disabled="!exists(sports)"
						:options="getOptionsFromObject(sports)"
						:state="invalidState(context.id + '_sport_activity')"
						@input="invalidDirty(context.id + '_sport_activity')">
						<template #first>
							<b-form-select-option :value="null" disabled>-- Select Sport --</b-form-select-option>
						</template>
					</b-form-select>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_sport_activity') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</b-col>
			<b-col lg="6">
				<b-form-group label="Sport Type">
					<b-form-select
						v-model="form.sport_type"
						name="sport_type"
						:disabled="!exists(sports)"
						:options="getOptionsFromObject(sport_types)"
						:state="invalidState(context.id + '_sport_type')"
						@input="invalidDirty(context.id + '_sport_type')">
						<template #first>
							<b-form-select-option :value="null" disabled>-- Select Sport Type --</b-form-select-option>
						</template>
					</b-form-select>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_sport_type') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</b-col>
		</b-row>

		<!-- Hide from camps and tournaments unless data already exists) -->
		<b-row v-if="(form.sport_type !== 'camp' && form.sport_type !== 'tournament') || form.sport_details">
			<b-col>
				<b-form-group label="Details (optional)">
					<b-form-textarea
						v-model.lazy="form.sport_details"
						:name="'sport_details'"
						:disabled="!exists(sports)">
					</b-form-textarea>
				</b-form-group>
			</b-col>
		</b-row>

		<template v-if="form.sport_type && (form.sport_type === 'camp' || form.sport_type === 'tournament')">
			<b-form-group label="Event Name">
				<b-form-input
					v-model.trim="form.event_name"
					name="event_name"
					type="text"
					:state="invalidState(context.id + '_event_name')"
					@input="invalidDirty(context.id + '_event_name')"></b-form-input>

				<b-form-invalid-feedback>
					{{ invalidError(context.id + '_event_name') }}
				</b-form-invalid-feedback>
			</b-form-group>
			<b-row class="mb-4">
				<b-col>
					<FormulateInput
						v-model="form.tournament_address"
						type="full_address"
						name="tournament_address"
						:label="campOrTournamentLabel + ' Address'"
						:with-address-name="true" />
				</b-col>
			</b-row>
			<b-row class="mb-4">
				<b-col>
					<FormulateInput
						v-model="form.tournament_date"
						type="vcal_multiple"
						name="tournament_dates"
						:label="campOrTournamentLabel + ' Event Dates'" />
				</b-col>
			</b-row>
			<b-row>
				<b-col>
					<FormulateInput
						v-model="form.concussion_management_policy"
						type="yes_no_explain"
						label="Do you have a written concussion management policy that is compliant with current state legislation?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Concussion management policy confirmation" />
					<FormulateInput
						v-model="form.concussion_play_removal"
						type="yes_no_explain"
						label="Does your policy require that any participant suspected of sustaining a head injury be removed from play immediately?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Removal confirmation" />
					<FormulateInput
						v-model="form.concussion_medical_check"
						type="yes_no_explain"
						label="Does your policy require that any participant sustaining a head injury to be cleared by a licensed healthcare provider before the player is allowed to return to practices?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Medically cleared confirmation" />
					<FormulateInput
						v-model="form.concussion_policy_distribution"
						type="yes_no_explain"
						label="Do you distribute the written policy to coaches, parents and players and require parents’ written acknowledgment that they have received and reviewed?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Policy distribution confirmation" />
					<FormulateInput
						v-model="form.concussion_recognition"
						type="yes_no_explain"
						label="Do you require your coaches to undergo formal training (at minimum) for concussion recognition?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Recognition confirmation" />
					<FormulateInput
						v-model="form.subcontractors_liability_coverage"
						type="yes_no_explain"
						label="Do you require all vendors/exhibitor and subcontractors to provide you with valid proof of liability insurance, showing You listed as additional insured?"
						help="Minimum $1,000,000 occurrence / $2,000,000 aggregate limits"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Vendors/exhibitor and subcontractors liability insurance confirmation" />
					<p v-if="form.subcontractors_liability_coverage === 'no'" class="mb-4 border-left pl-4">
						<em>
							Please note, there is a warranty in your policy that states that you are required to follow this practice, or coverage will not apply to any claim arising out of any subcontractor’s, vendor’s or exhibitors liability.
						</em>
					</p>
					<FormulateInput
						v-if="form.sport_type === 'tournament'"
						v-model="form.guests_carry_valid_liability"
						type="yes_no_explain"
						label="Do you require all guests and visiting teams to carry their own valid liability insurance, listing your organization as an additional insured?"
						outer-class="form-group"
						input-class="form-control"
						validation="required"
						validation-name="Guest liability coverage confirmation" />

					<b-alert :show="! hasConcussion" variant="danger">
						Please note, based on your answer, your policy will contain a concussion and head injury exclusion. Contact us to review or discuss the exclusion.
					</b-alert>
				</b-col>
			</b-row>
		</template>

		<b-row>
			<b-col cols="12" md="6">
				<b-form-group label="Sport Level" description="...including games, practices and tournaments (for period of coverage)">
					<b-form-select
						v-model="form['sport_level']"
						name="sport_level"
						:disabled="!exists(sports)"
						:options="getOptionsFromObject(levels)"
						:state="invalidState(context.id + '_sport_level')"
						@input="invalidDirty(context.id + '_sport_level')">
						<template #first>
							<b-form-select-option :value="null" disabled>-- Select Sport Level --</b-form-select-option>
						</template>
					</b-form-select>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_sport_level') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</b-col>
			<b-col cols="12" md="6">
				<b-form-group label="Number of Days">
					<b-form-input
						v-model.trim="form.sport_days"
						name="sport_days"
						placeholder="0"
						type="number"
						:state="invalidState(context.id + '_sport_days')"
						@input="invalidDirty(context.id + '_sport_days')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_sport_days') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</b-col>

			<b-col v-if="form.sport_type && (form.sport_type === 'camp' || form.sport_type === 'tournament')">
				<b-form-group label="Max # of Participants Per Day">
					<b-form-input
						v-model.trim="form.participants_per_day"
						name="participants_per_day"
						placeholder="0"
						type="number"
						:state="invalidState(context.id + '_participants_per_day')"
						@input="invalidDirty(context.id + '_participants_per_day')"></b-form-input>

					<b-form-invalid-feedback>
						{{ invalidError(context.id + '_participants_per_day') }}
					</b-form-invalid-feedback>
				</b-form-group>
			</b-col>
		</b-row>
		<b-row>
			<b-col>
				<b-table-simple
					bordered
					dark
					hover
					responsive="md"
					stacked="sm">
					<b-thead>
						<b-tr>
							<b-th>Group</b-th>
							<b-th style="width: 33%;">Number of Participants</b-th>
							<b-th style="width: 33%;">Number of Teams</b-th>
						</b-tr>
					</b-thead>
					<b-tbody>
						<template v-for="(ageGroup, index) in tableRows">
							<b-tr :key="`row-` + index">
								<b-td><strong>{{ ageGroup.label }}</strong></b-td>
								<b-td stacked-heading="Participants">
									<b-form-group class="mb-0">
										<b-form-input
											v-model.trim="form['sport_participants_' + ageGroup.age]"
											:formatter="$options.filters.integer"
											:name="'sport_participants_' + ageGroup.age"
											placeholder="# of participants"
											type="number"
											min="0"
											:state="invalidState(context.id + '_sport_participants_' + ageGroup.age)"
											@input="invalidDirty(context.id + '_sport_participants_' + ageGroup.age)" />

										<b-form-invalid-feedback>
											{{ invalidError(context.id + '_sport_participants_' + ageGroup.age) }}
											Enter 0 if there are none.
										</b-form-invalid-feedback>
									</b-form-group>
								</b-td>
								<b-td stacked-heading="Teams">
									<b-form-group class="mb-0">
										<b-form-input
											v-model.trim="form['sport_teams_' + ageGroup.age]"
											:formatter="$options.filters.integer"
											:name="'sport_teams_' + ageGroup.age"
											placeholder="# of teams"
											type="number"
											min="0"
											:state="invalidState(context.id + '_sport_teams_' + ageGroup.age)"
											@input="invalidDirty(context.id + '_sport_teams_' + ageGroup.age)" />

										<b-form-invalid-feedback>
											{{ invalidError(context.id + '_sport_teams_' + ageGroup.age) }}
											Enter 0 if there are none.
										</b-form-invalid-feedback>
									</b-form-group>
								</b-td>
							</b-tr>
						</template>
					</b-tbody>
				</b-table-simple>
			</b-col>
		</b-row>
		<b-row v-if="totalTeams <= 0 || totalParticipants <= 0">
			<b-col>
				<b-alert v-if="totalParticipants <= 0" show variant="warning">You must enter at least one participant for each team.</b-alert>
				<b-alert v-if="totalTeams <= 0" show variant="warning">You must enter at least one team.</b-alert>
			</b-col>
		</b-row>
		<b-row>
			<b-col lg="6">
				<b-form-group label="Total Gross Receipts">
					<b-input-group prepend="$">
						<b-form-input
							v-model.trim="form.sport_gross_receipts"
							:formatter="$options.filters.numeric"
							name="sport_gross_receipts"
							placeholder="0"
							:state="invalidState(context.id + '_sport_gross_receipts')"
							@input="invalidDirty(context.id + '_sport_gross_receipts')" />

						<b-form-invalid-feedback>
							{{ invalidError(context.id + '_sport_gross_receipts') }}
						</b-form-invalid-feedback>
					</b-input-group>
				</b-form-group>
			</b-col>
		</b-row>
	</div>
</template>
<script>
import _ from 'lodash'
import formInvalid from '@/Pages/Forms/Mixins/FormInvalid'
import selector from '@/Mixin/selector'
import {mapState} from 'vuex'

export default {
	mixins: [formInvalid, selector],
	props: {
		context: {
			type: Object,
			required: true
		}
	},
	data () {
		return {
			applicationType: 'XX',
			version: '2022-10-31',
			form: {
				version: this.version,
				attributes: this.context.attributes,

				sport_activity: null,
				sport_type: null,
				sport_details: '',
				sport_level: null,
				sport_days: null,
				participants_per_day: null,
				sport_gross_receipts: null,

				// camp and tournaments
				tournament_date: null,
				event_name: null,
				concussion_management_policy: null,
				concussion_play_removal: null,
				concussion_medical_check: null,
				concussion_policy_distribution: null,
				concussion_recognition: null,
				subcontractors_liability_coverage: null,
				guests_carry_valid_liability: null,
				tournament_address: {}
			},

			sports: [],
			sport_types: {
				'team': 'Team',
				'league': 'League and/or Club',
				'association': 'Association',
				'tournament': 'Event and/or Tournament',
				'camp': 'Camp/Clinic',
				'other': 'Other'
			},
			levels: {
				'amateur': 'Amateur',
				'semi-pro': 'Semi-Professional',
				'pro': 'Professional'
			},
			label: 'Sport',
			validationMessages: {
				'min': 'You must add at least one activity.'
			},

			// This is used to set up the table, fields, and their validations
			// Any changes here should also be reflected in the form object
			// the object key here is used at the end of the form key: 10 = sport_participants_10, etc..
			tableRows: [],
			participantFields: [],
			teamFields: []
		}
	},
	computed: {
		...mapState({
			submitted: state => state.application.submitted
		}),
		maxParticipantsPerTeam () {
			if (this.context && this.context.attributes['max_participants'] !== undefined) {
				return this.context.attributes['max_participants']
			}

			return 9999
		},
		totalParticipants () {
			let totalParticipants = 0

			for (const fieldKey in this.participantFields) {
				if (!this.participantFields.hasOwnProperty(fieldKey)) continue
				const fieldName = this.participantFields[fieldKey]
				if (!this.form.hasOwnProperty(fieldName) || !this.form[fieldName]) continue
				totalParticipants += parseInt(this.form[fieldName])
			}

			return totalParticipants
		},
		totalTeams () {
			let totalTeams = 0

			for (const teamFieldsKey in this.teamFields) {
				if (!this.teamFields.hasOwnProperty(teamFieldsKey)) continue
				const fieldName = this.teamFields[teamFieldsKey]
				if (!this.form.hasOwnProperty(fieldName) || ! this.form[fieldName]) continue
				totalTeams += parseInt(this.form[fieldName])
			}

			return totalTeams
		},
		hasConcussion () {
			const requirements = [
				this.form.concussion_management_policy,
				this.form.concussion_play_removal,
				this.form.concussion_medical_check,
				this.form.concussion_policy_distribution,
				this.form.concussion_recognition,
				this.form.subcontractors_liability_coverage,
				this.form.guests_carry_valid_liability
			]

			return Object.values(requirements).every(value => value !== 'no')
		},
		campOrTournamentLabel () {
			if (this.form.sport_type === 'camp') {
				return 'Camp/Clinic'
			}

			if (this.form.sport_type === 'tournament') {
				return 'Tournament'
			}

			return 'Event'
		}
	},
	watch: {
		'context.attributes' (newAttributes) {
			this.form.attributes = newAttributes
		},
		submitted () {
			for (const field in this.form) {
				if (!this.form.hasOwnProperty(field)) continue
				this.$store.commit('setInvalidDirty', this.context.id + '_' + field)
			}
		},
		form: {
			handler: _.debounce(function (formData) {
				const modelData = Object.assign({}, formData)
				this.$set(this.context, 'model', modelData)

				if (this.totalParticipants === 0 && this.totalTeams === 0) {
					this.$store.commit('setInvalid', {
						field: this.context.id + '_no_participants_no_teams',
						error: 'No participants or teams have been added, add some to continue.'
					})
				} else {
					this.invalidReset(this.context.id + '_no_participants_no_teams')
				}
			}, 1000),
			deep: true
		},
		totalParticipants (newTotal) {
			if (newTotal === 0) {
				this.$store.commit('setInvalid', {
					field: this.context.id + '_total_participants',
					error: 'Must enter at least 1 participant'
				})
			} else {
				this.invalidReset(this.context.id + '_total_participants')
			}
		},
		totalTeams (newTotal) {
			if (newTotal === 0) {
				this.$store.commit('setInvalid', {
					field: this.context.id + '_total_teams',
					error: 'Must enter at least 1 team'
				})
			} else {
				this.invalidReset(this.context.id + '_total_teams')
			}
		}
	},
	beforeDestroy () {
		for (const field in this.form) {
			if (!this.form.hasOwnProperty(field)) continue
			this.invalidReset(this.context.id + '_' + field)
		}
	},
	mounted () {
		if (this.$page.props.hasOwnProperty('form')) {
			this.applicationType = this.$page.props.form.type
		} else if (this.$page.props.hasOwnProperty('application')) {
			this.applicationType = this.$page.props.application.type.abbr
		}

		if (this.context.attributes.hasOwnProperty('ages')) {
			this.context.attributes.ages.forEach(ageGroup => {
				const fieldSuffix = `${ageGroup.minAge ?? ''}${ageGroup.maxAge && ageGroup.minAge ? '_' : ''}${ageGroup.maxAge ?? ''}`
				const participantField = `sport_participants_${fieldSuffix}`
				const teamField = `sport_teams_${fieldSuffix}`

				this.tableRows.push({
					label: ageGroup.name,
					age: fieldSuffix
				})

				this.participantFields.push(participantField)
				this.teamFields.push(teamField)

				this.$set(this.form, participantField, null)
				this.$set(this.form, teamField, null)
			})
		}

		// Validate each field via watcher when it changes
		for (const field in this.form) {
			if (!this.form.hasOwnProperty(field)) continue

			this.$watch(vm => [vm.form[field]], _.debounce(function () {
				this.validate(field)
			}, 250))
		}

		// Additional watchers for each participant and age group ensures minimums are met
		// this just helps avoid user confusion when adding or removing data when starting with "0" values
		// for example, one participant when starting with 0 should have at least 1 team added if no teams exist already
		_.forEach(this.tableRows, ageGroup => {
			this.$watch(vm => [vm.form['sport_participants_' + ageGroup.age]], function (newValue) {
				if (newValue >= 1 && parseInt(this.form['sport_teams_' + ageGroup.age]) === 0) {
					this.form['sport_teams_' + ageGroup.age] = 1
				}
			})

			this.$watch(vm => [vm.form['sport_teams_' + ageGroup.age]], function (newValue) {
				if (parseInt(newValue) === 0) {
					this.form['sport_participants_' + ageGroup.age] = 0
				}
			})
		})

		if (this.context.attributes.hasOwnProperty('sports')) {
			this.sports = this.context.attributes['sports']

			// Pre-select if there's only one type of activity available
			if (this.length(this.sports) === 1) {
				this.$set(this.form, 'sport_activity', Object.keys(this.sports)[0])
			}
		}

		if (this.context.attributes.hasOwnProperty('sport_types')) {
			this.sport_types = this.context.attributes['sport_types']

			// Pre-select if there's only one type of activity available
			if (this.length(this.sport_types) === 1) {
				this.$set(this.form, 'sport_type', Object.keys(this.sport_types)[0])
			}
		}

		// Formulate does not expect an object for the input "field" value
		// Here we set the defaults:
		if (typeof this.context.model !== 'object') {
			this.$set(this.context, 'model', this.form)
		} else if (typeof this.context.model === 'object') {
			// Except if we're editing existing data, let's copy it back into the local object
			// !! avoid updating context directly as much as possible
			const modelData = Object.assign({}, this.context.model)
			for (const formKey in this.form) {
				if (! this.form.hasOwnProperty(formKey)) continue
				if (! modelData.hasOwnProperty(formKey)) continue
				this.form[formKey] = modelData[formKey]
			}
		}

		this.$set(this.form, 'version', this.version)
		this.$set(this.context.model, 'version', this.version)

		// Validate all re-populated field data
		this.validate()
		this.$nextTick(() => this.$set(this.context, 'model', this.form))

		// "none" was removed from sport levels, this will ensure the field isn't blank for older forms
		if (this.form.sport_level && !this.levels.hasOwnProperty(this.form.sport_level)) {
			this.levels[this.form.sport_level] = this.form.sport_level
		}

		if (this.exists(this.context.attributes.facade)) {
			this.label = this.context.attributes.facade
		}
	},
	methods: {
		validate (fieldName = null) {
			// Define validation functions for fields
			// Most of them are just "required" but can optionally also check other validations
			let fields = {
				'sport_activity': () => this.validateRequired('Activity', this.context.id + '_sport_activity', this.form['sport_activity']),
				'sport_days': () => this.validateRequired('Number of playing days', this.context.id + '_sport_days', this.form['sport_days']),
				'sport_level': () => this.validateRequired('Sport Level', this.context.id + '_sport_level', this.form['sport_level']),
				'sport_type': () => this.validateRequired('Sport Type', this.context.id + '_sport_type', this.form['sport_type']),
				'participants_per_day': () => {
					if (['camp', 'tournament'].includes(this.form.sport_type)) {
						this.validateRequired('Max number of participants per day', this.context.id + '_participants_per_day', this.form['participants_per_day'])
					} else {
						this.invalidReset(this.context.id + '_participants_per_day')
					}
				},'event_name': () => {
					if (['camp', 'tournament'].includes(this.form.sport_type)) {
						this.validateRequired('Event name is required', this.context.id + '_event_name', this.form['event_name'])
					} else {
						this.invalidReset(this.context.id + '_event_name')
					}
				},
				'sport_gross_receipts': () => this.validateRequired('Total Gross Receipts', this.context.id + '_sport_gross_receipts', this.form['sport_gross_receipts'])
			}

			// Validation for all the table row input fields
			_.forEach(this.tableRows, ageGroup => {
				fields['sport_participants_' + ageGroup.age] = () => this.validateRange(
					'Number of participants ' + ageGroup.label,
					this.context.id + '_sport_participants_' + ageGroup.age,
					this.form['sport_participants_' + ageGroup.age],
					this.form['sport_teams_' + ageGroup.age] >= 1 ? 1 : 0,
					this.form['sport_teams_' + ageGroup.age] * this.maxParticipantsPerTeam
				)

				fields['sport_teams_' + ageGroup.age] = () => {
					this.validateRange(
						'Number of teams with participants ' + ageGroup.label,
						this.context.id + '_sport_teams_' + ageGroup.age,
						this.form['sport_teams_' + ageGroup.age]
					)

					this.validate('sport_participants_' + ageGroup.age)
				}
			})

			// Checking only one field at a time
			if (fieldName) {
				if (fields.hasOwnProperty(fieldName) && typeof fields[fieldName] === 'function') {
					fields[fieldName].call()
				}

				return
			}

			for (const requiredKey in fields) {
				if (!fields.hasOwnProperty(requiredKey)) continue
				if(typeof fields[requiredKey] === 'function') {
					fields[requiredKey].call()
				}
			}
		}
	}
}
</script>
